import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSpaces } from '../context/SpaceContext';
import { listUsers } from '../services/userService';
import { createProject, getProjects } from '../services/projectService';
import {
  addBusinessMember,
  addBusinessTeam,
  approveBusinessJoin,
  createBusiness,
  getBusinesses,
  getBusiness,
  linkBusinessProject,
  linkBusinessSpace,
  rejectBusinessJoin,
  removeBusinessMember,
  removeBusinessTeam,
  requestBusinessJoin,
  unlinkBusinessSpace,
  updateBusiness,
  updateBusinessMember,
  getBusinessRelationships,
  createBusinessRelationship,
  archiveBusinessRelationship,
  getBusinessAccountingOverview,
  getBusinessDomains,
  createBusinessDomain,
  verifyBusinessDomain,
  getBusinessAddressRoutes,
  createBusinessAddressRoute,
} from '../services/businessService';
import {
  getBusinessRoleAssignments,
  createRoleAssignment,
  revokeRoleAssignment,
} from '../services/roleAssignmentService';
import {
  getFundingSources,
  createFundingSource,
  createFundingAllocation,
} from '../services/fundingService';
import {
  getBusinessReportingObligations,
  createReportingObligation,
  updateReportingObligationStatus,
} from '../services/reportingObligationService';
import { getVisibleBusinessTabs, hasAnyCapability } from '../utils/businessWorkspaceNav';
import './BusinessesPage.css';

const BUSINESS_ROLE_OPTIONS = ['owner', 'admin', 'member', 'viewer'];
const ROLE_ASSIGNMENT_ROLE_OPTIONS = ['owner', 'admin', 'member', 'viewer', 'accounting', 'project-manager'];
const ACCESS_CONTEXT_TYPES = ['business', 'project', 'space'];
const RELATIONSHIP_TYPES = [
  'partner', 'client', 'customer', 'funder', 'government',
  'donor', 'vendor', 'supplier', 'consultant', 'contractor',
  'affiliate', 'service-provider',
];

const LIFECYCLE_LABELS = {
  idea: 'Idea',
  'funding-review': 'Funding Review',
  'funding-pending': 'Funding Pending',
  approved: 'Approved',
  active: 'Active',
  'delivery-complete': 'Delivery Complete',
  reporting: 'Reporting',
  closed: 'Closed',
  'on-hold': 'On Hold',
  cancelled: 'Cancelled',
};

const formatLifecycle = (state) => LIFECYCLE_LABELS[state] || state || 'Active';
const JOIN_POLICY_LABELS = {
  request: 'Requests reviewed',
  open: 'Open to join',
  invite: 'Invite only',
};

const formatJoinPolicy = (policy) => JOIN_POLICY_LABELS[policy] || 'Requests reviewed';
const formatAccessScope = (scopeType) => ({ business: 'This Business', project: 'Project', space: 'Space' }[scopeType] || 'Access');

const tabLabel = (tab) => ({ money: 'Money' }[tab] || tab.charAt(0).toUpperCase() + tab.slice(1));

const initialBusinessForm = {
  name: '',
  description: '',
  website: '',
  industry: '',
  joinPolicy: 'request',
};

export default function BusinessesPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { spaces = [] } = useSpaces() || {};

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewBusiness, setShowNewBusiness] = useState(false);
  const [newBusiness, setNewBusiness] = useState(initialBusinessForm);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');

  const [allUsers, setAllUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(initialBusinessForm);
  const [joinMessage, setJoinMessage] = useState('');
  const [memberUserId, setMemberUserId] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const [memberTeamId, setMemberTeamId] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [linkSpaceId, setLinkSpaceId] = useState('');
  const [linkProjectId, setLinkProjectId] = useState('');
  const [updatingMemberId, setUpdatingMemberId] = useState('');
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [savingProject, setSavingProject] = useState(false);
  const currentUserId = user?._id;

  // ── Tab States ──
  // Relationships
  const [relationships, setRelationships] = useState([]);
  const [relTargetBizId, setRelTargetBizId] = useState('');
  const [relType, setRelType] = useState('partner');
  const [relLoading, setRelLoading] = useState(false);

  // Scoped Role Assignments
  const [roleAssignments, setRoleAssignments] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [newRoleUserId, setNewRoleUserId] = useState('');
  const [newRoleScopeType, setNewRoleScopeType] = useState('project');
  const [newRoleScopeId, setNewRoleScopeId] = useState('');
  const [newRoleName, setNewRoleName] = useState('project-manager');
  const [newRoleValidDays, setNewRoleValidDays] = useState(30);

  // Funding Sources & Allocations
  const [fundingSources, setFundingSources] = useState([]);
  const [fundingLoading, setFundingLoading] = useState(false);
  const [newFundingName, setNewFundingName] = useState('');
  const [newFundingBody, setNewFundingBody] = useState('');
  const [newFundingAmount, setNewFundingAmount] = useState('');
  const [newFundingRef, setNewFundingRef] = useState('');
  const [allocSourceId, setAllocSourceId] = useState('');
  const [allocProjectId, setAllocProjectId] = useState('');
  const [allocAmount, setAllocAmount] = useState('');

  // Accounting Hub
  const [accountingOverview, setAccountingOverview] = useState(null);
  const [accountingLoading, setAccountingLoading] = useState(false);

  // Reporting Obligations
  const [obligations, setObligations] = useState([]);
  const [obligationsLoading, setObligationsLoading] = useState(false);
  const [newObligationTitle, setNewObligationTitle] = useState('');
  const [newObligationType, setNewObligationType] = useState('quarterly-expenditure');
  const [newObligationProjectId, setNewObligationProjectId] = useState('');
  const [newObligationDueDate, setNewObligationDueDate] = useState('');

  // Domains & Routing
  const [domains, setDomains] = useState([]);
  const [addressRoutes, setAddressRoutes] = useState([]);
  const [domainLoading, setDomainLoading] = useState(false);
  const [newDomainName, setNewDomainName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newAddressDomainId, setNewAddressDomainId] = useState('');
  const [newAddressTargetType, setNewAddressTargetType] = useState('space');
  const [newAddressTargetId, setNewAddressTargetId] = useState('');

  const refreshBusiness = useCallback(async (id) => {
    const res = await getBusiness(id);
    const nextBusiness = res.data.business || null;
    const spacesForBusiness = Array.isArray(res.data.spaces) ? res.data.spaces : [];
    const projectsForBusiness = Array.isArray(res.data.projects) ? res.data.projects : [];
    const hydrated = nextBusiness ? { ...nextBusiness, spaces: spacesForBusiness, projects: projectsForBusiness } : null;
    setSelectedBusiness(hydrated);
    return hydrated;
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getBusinesses(), listUsers(), getProjects()])
      .then(([businessRes, usersRes, projectsRes]) => {
        if (!active) return;
        const list = businessRes.data.businesses || [];
        setBusinesses(list);
        setAllUsers(usersRes.data.users || []);
        setProjects(projectsRes.data.projects || []);
        if (businessId) {
          const found = list.find((b) => b._id === businessId);
          if (found) {
            setSelectedBusiness(found);
            refreshBusiness(found._id).catch((err) => console.error('Failed to load business', err));
          }
        }
      })
      .catch((err) => console.error('Failed to load businesses:', err))
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [businessId, refreshBusiness]);

  // Load active tab data
  useEffect(() => {
    if (!selectedBusiness) return;

    const capabilities = selectedBusiness.currentUserCapabilities || [];
    const canViewFunding = hasAnyCapability(capabilities, ['funding.view', 'funding.manage']);
    const canViewAccounting = hasAnyCapability(capabilities, ['accounting.view', 'accounting.manage']);
    const canViewReporting = hasAnyCapability(capabilities, ['reporting.view', 'reporting.manage']);
    const canManageDomains = hasAnyCapability(capabilities, ['domain.manage', 'address.manage']);
    const loaderOwnerId = selectedBusiness.owner?._id || selectedBusiness.owner;
    const loaderBusinessRole = String(loaderOwnerId) === String(currentUserId)
      ? 'owner'
      : (selectedBusiness.members || []).find((m) => String(m.user?._id || m.user) === String(currentUserId))?.role || null;
    const canLoadBusinessAdmin = loaderBusinessRole === 'owner' || loaderBusinessRole === 'admin';

    if (detailTab === 'money') {
      if (canViewFunding) {
        setFundingLoading(true);
        getFundingSources(selectedBusiness._id)
          .then((res) => setFundingSources(res.data.fundingSources || res.data.sources || []))
          .catch((err) => console.error('Failed to load funding sources:', err))
          .finally(() => setFundingLoading(false));
      }
      if (canViewAccounting) {
        setAccountingLoading(true);
        getBusinessAccountingOverview(selectedBusiness._id)
          .then((res) => setAccountingOverview(res.data.overview || null))
          .catch((err) => console.error('Failed to load accounting overview:', err))
          .finally(() => setAccountingLoading(false));
      }
      if (canViewReporting) {
        setObligationsLoading(true);
        getBusinessReportingObligations(selectedBusiness._id)
          .then((res) => setObligations(res.data.obligations || []))
          .catch((err) => console.error('Failed to load obligations:', err))
          .finally(() => setObligationsLoading(false));
      }
    } else if (detailTab === 'more') {
      if (canLoadBusinessAdmin) {
        setRelLoading(true);
        getBusinessRelationships(selectedBusiness._id)
          .then((res) => setRelationships(res.data.relationships || []))
          .catch((err) => console.error('Failed to load relationships:', err))
          .finally(() => setRelLoading(false));
        setRoleLoading(true);
        getBusinessRoleAssignments(selectedBusiness._id)
          .then((res) => setRoleAssignments(res.data.assignments || []))
          .catch((err) => console.error('Failed to load additional access:', err))
          .finally(() => setRoleLoading(false));
      }
      if (canManageDomains) {
        setDomainLoading(true);
        Promise.all([
          getBusinessDomains(selectedBusiness._id),
          getBusinessAddressRoutes(selectedBusiness._id),
        ])
          .then(([domRes, routeRes]) => {
            setDomains(domRes.data.domains || []);
            setAddressRoutes(routeRes.data.routes || []);
          })
          .catch((err) => console.error('Failed to load domains/routes:', err))
          .finally(() => setDomainLoading(false));
      }
    }
  }, [selectedBusiness, detailTab, currentUserId]);

  const visibleUsers = useMemo(() => {
    const members = selectedBusiness?.members || [];
    const memberIds = new Set(members.map((member) => String(member.user?._id || member.user)));
    return allUsers.filter((u) => !memberIds.has(String(u._id)));
  }, [allUsers, selectedBusiness]);

  const selectedTeams = selectedBusiness?.teams || [];
  const selectedRequests = selectedBusiness?.joinRequests || [];

  useEffect(() => {
    if (selectedBusiness) {
      setEditData({
        name: selectedBusiness.name || '',
        description: selectedBusiness.description || '',
        website: selectedBusiness.website || '',
        industry: selectedBusiness.industry || '',
        joinPolicy: selectedBusiness.joinPolicy || 'request',
      });
    }
  }, [selectedBusiness]);

  const openBusiness = async (b) => {
    navigate(`/businesses/${b._id}`, { replace: true });
    try {
      await refreshBusiness(b._id);
    } catch (err) {
      console.error('Failed to open business:', err);
      setSelectedBusiness(b);
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    if (!newBusiness.name.trim()) return;
    setSavingBusiness(true);
    try {
      const res = await createBusiness(newBusiness);
      const created = res.data.business;
      setBusinesses((prev) => [created, ...prev]);
      setNewBusiness(initialBusinessForm);
      setShowNewBusiness(false);
      await openBusiness(created);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create business');
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleSaveBusiness = async (e) => {
    e.preventDefault();
    if (!selectedBusiness) return;
    try {
      const res = await updateBusiness(selectedBusiness._id, editData);
      const updated = res.data.business;
      setBusinesses((prev) => prev.map((b) => (b._id === updated._id ? updated : b)));
      await refreshBusiness(updated._id);
      setEditing(false);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update business');
    }
  };

  const handleRequestJoin = async (e) => {
    e.preventDefault();
    if (!selectedBusiness) return;
    try {
      const res = await requestBusinessJoin(selectedBusiness._id, { message: joinMessage });
      await refreshBusiness(res.data.business._id);
      setJoinMessage('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to request join');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !memberUserId) return;
    try {
      await addBusinessMember(selectedBusiness._id, memberUserId, memberRole, memberTeamId || null);
      setMemberUserId('');
      setMemberRole('member');
      setMemberTeamId('');
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedBusiness) return;
    try {
      await removeBusinessMember(selectedBusiness._id, userId);
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleUpdateMemberRole = async (targetUserId, role) => {
    if (!selectedBusiness || !targetUserId || !role) return;
    setUpdatingMemberId(String(targetUserId));
    try {
      await updateBusinessMember(selectedBusiness._id, targetUserId, { role });
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update member role');
    } finally {
      setUpdatingMemberId('');
    }
  };

  const handleAddTeam = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !teamName.trim()) return;
    try {
      await addBusinessTeam(selectedBusiness._id, { name: teamName, description: teamDescription });
      setTeamName('');
      setTeamDescription('');
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add team');
    }
  };

  const handleRemoveTeam = async (teamIndex) => {
    if (!selectedBusiness) return;
    try {
      await removeBusinessTeam(selectedBusiness._id, teamIndex);
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove team');
    }
  };

  const handleApproveRequest = async (requesterId) => {
    if (!selectedBusiness) return;
    try {
      await approveBusinessJoin(selectedBusiness._id, requesterId, {});
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to approve request');
    }
  };

  const handleRejectRequest = async (requesterId) => {
    if (!selectedBusiness) return;
    try {
      await rejectBusinessJoin(selectedBusiness._id, requesterId);
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to reject request');
    }
  };

  const handleLinkSpace = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !linkSpaceId) return;
    try {
      await linkBusinessSpace(selectedBusiness._id, linkSpaceId);
      setLinkSpaceId('');
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to link space');
    }
  };

  const handleUnlinkSpace = async (spaceId) => {
    if (!selectedBusiness) return;
    try {
      await unlinkBusinessSpace(selectedBusiness._id, spaceId);
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to unlink space');
    }
  };

  const handleLinkProject = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !linkProjectId) return;
    try {
      await linkBusinessProject(selectedBusiness._id, linkProjectId);
      setLinkProjectId('');
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add this Business as a participant');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !newProjectName.trim()) return;
    setSavingProject(true);
    try {
      const res = await createProject({
        name: newProjectName.trim(),
        description: newProjectDescription.trim(),
        leadBusinessId: selectedBusiness._id,
        lifecycleState: 'idea',
      });
      const created = res.data.project;
      setProjects((prev) => [created, ...prev]);
      setNewProjectName('');
      setNewProjectDescription('');
      setShowNewProject(false);
      await refreshBusiness(selectedBusiness._id);
      navigate(`/projects/${created._id}`);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create project');
    } finally {
      setSavingProject(false);
    }
  };

  // ── Relationship Actions ──
  const handleAddRelationship = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !relTargetBizId) return;
    try {
      const res = await createBusinessRelationship(selectedBusiness._id, {
        relatedBusinessId: relTargetBizId,
        relationshipTypes: [relType],
      });
      setRelationships((prev) => [res.data.relationship, ...prev]);
      setRelTargetBizId('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create relationship');
    }
  };

  const handleArchiveRelationship = async (relId) => {
    if (!window.confirm('Archive this business relationship?')) return;
    try {
      await archiveBusinessRelationship(selectedBusiness._id, relId);
      setRelationships((prev) => prev.filter((r) => r._id !== relId));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to archive relationship');
    }
  };

  // ── Role Assignment Actions ──
  const handleCreateRoleAssignment = async (e) => {
    e.preventDefault();
    const scopeId = newRoleScopeType === 'business' ? selectedBusiness?._id : newRoleScopeId;
    if (!selectedBusiness || !newRoleUserId || !scopeId) return;
    try {
      const validUntil = new Date(Date.now() + newRoleValidDays * 86400000).toISOString();
      const res = await createRoleAssignment(selectedBusiness._id, {
        userId: newRoleUserId,
        scopeType: newRoleScopeType,
        scopeId,
        role: newRoleName,
        validUntil,
      });
      setRoleAssignments((prev) => [res.data.assignment, ...prev]);
      setNewRoleUserId('');
      setNewRoleScopeId('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create role assignment');
    }
  };

  const handleRevokeRole = async (assignmentId) => {
    if (!window.confirm('Revoke this role assignment immediately? Access will end promptly.')) return;
    try {
      await revokeRoleAssignment(selectedBusiness._id, assignmentId);
      setRoleAssignments((prev) =>
        prev.map((a) => (a._id === assignmentId ? { ...a, revokedAt: new Date().toISOString() } : a))
      );
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove access');
    }
  };

  // ── Funding Actions ──
  const handleCreateFundingSource = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !newFundingName.trim() || !newFundingAmount) return;
    try {
      const res = await createFundingSource(selectedBusiness._id, {
        name: newFundingName.trim(),
        fundingBody: newFundingBody.trim() || 'General Funding',
        totalAmount: Number(newFundingAmount),
        agreementReference: newFundingRef.trim(),
      });
      setFundingSources((prev) => [res.data.fundingSource, ...prev]);
      setNewFundingName('');
      setNewFundingBody('');
      setNewFundingAmount('');
      setNewFundingRef('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create funding source');
    }
  };

  const handleCreateAllocation = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !allocSourceId || !allocProjectId || !allocAmount) return;
    try {
      await createFundingAllocation(selectedBusiness._id, allocSourceId, {
        projectId: allocProjectId,
        allocatedAmount: Number(allocAmount),
      });
      alert('Funding allocated to project successfully.');
      setAllocAmount('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to allocate funding');
    }
  };

  // ── Reporting Obligations Actions ──
  const handleCreateObligation = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !newObligationTitle.trim() || !newObligationProjectId || !newObligationDueDate) return;
    try {
      const res = await createReportingObligation(selectedBusiness._id, {
        projectId: newObligationProjectId,
        title: newObligationTitle.trim(),
        type: newObligationType,
        dueDate: newObligationDueDate,
      });
      setObligations((prev) => [res.data.obligation, ...prev]);
      setNewObligationTitle('');
      setNewObligationDueDate('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create reporting obligation');
    }
  };

  const handleUpdateObligationStatus = async (obId, status) => {
    try {
      const res = await updateReportingObligationStatus(obId, { status });
      setObligations((prev) => prev.map((o) => (o._id === obId ? res.data.obligation : o)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update obligation status');
    }
  };

  // ── Domain Actions ──
  const handleCreateDomain = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !newDomainName.trim()) return;
    try {
      const res = await createBusinessDomain(selectedBusiness._id, { domainName: newDomainName.trim() });
      setDomains((prev) => [res.data.domain, ...prev]);
      setNewDomainName('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add domain');
    }
  };

  const handleVerifyDomain = async (domainId) => {
    try {
      const res = await verifyBusinessDomain(selectedBusiness._id, domainId);
      setDomains((prev) => prev.map((d) => (d._id === domainId ? res.data.domain : d)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to verify domain');
    }
  };

  const handleCreateAddressRoute = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !newAddress.trim() || !newAddressTargetId) return;
    try {
      const res = await createBusinessAddressRoute(selectedBusiness._id, {
        address: newAddress.trim(),
        domainId: newAddressDomainId || null,
        targetType: newAddressTargetType,
        targetId: newAddressTargetId,
      });
      setAddressRoutes((prev) => [res.data.route, ...prev]);
      setNewAddress('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create address route');
    }
  };

  const isMember = Boolean(selectedBusiness?.members?.some((m) => String(m.user?._id || m.user) === String(currentUserId)));
  const ownerId = selectedBusiness?.owner?._id || selectedBusiness?.owner;
  const ownerIds = (() => {
    const ids = new Set();
    if (ownerId) ids.add(String(ownerId));
    for (const m of selectedBusiness?.members || []) {
      if (m.role === 'owner') ids.add(String(m.user?._id || m.user));
    }
    return ids;
  })();
  const ownerCount = ownerIds.size;
  const currentBusinessRole = String(ownerId) === String(currentUserId)
    ? 'owner'
    : (selectedBusiness?.members || []).find((m) => String(m.user?._id || m.user) === String(currentUserId))?.role || null;
  const canManageMembers = currentBusinessRole === 'owner' || currentBusinessRole === 'admin';
  const currentCapabilities = selectedBusiness?.currentUserCapabilities || [];
  const canManageProjects = currentCapabilities.includes('project.manage');
  const canViewFunding = hasAnyCapability(currentCapabilities, ['funding.view', 'funding.manage']);
  const canManageFunding = currentCapabilities.includes('funding.manage');
  const canViewAccounting = hasAnyCapability(currentCapabilities, ['accounting.view', 'accounting.manage']);
  const canViewReporting = hasAnyCapability(currentCapabilities, ['reporting.view', 'reporting.manage']);
  const canManageReporting = currentCapabilities.includes('reporting.manage');
  const canManageDomains = hasAnyCapability(currentCapabilities, ['domain.manage', 'address.manage']);
  const visibleTabs = getVisibleBusinessTabs(currentCapabilities, canManageMembers);
  const recentSpaces = (selectedBusiness?.spaces || []).slice(0, 4);
  const keyPeople = (selectedBusiness?.members || []).slice(0, 5);
  const activeProjects = (selectedBusiness?.projects || []).filter((project) => !['closed', 'cancelled'].includes(project.lifecycleState || project.status));
  const pendingJoinRequestCount = selectedRequests.filter((request) => request.status === 'pending').length;
  const roleOptionsForEditor = currentBusinessRole === 'owner' ? BUSINESS_ROLE_OPTIONS : BUSINESS_ROLE_OPTIONS.filter((r) => r !== 'owner');

  useEffect(() => {
    if (!selectedBusiness) return;
    if (!visibleTabs.includes(detailTab)) setDetailTab('overview');
  }, [detailTab, selectedBusiness, visibleTabs]);

  return (
    <div className="businesses-page">
      {/* ── Sidebar List ── */}
      <aside className="businesses-sidebar">
        <div className="businesses-sidebar-header">
          <h2 className="businesses-sidebar-title">Businesses</h2>
          <button className="btn btn-primary btn-sm" onClick={() => setShowNewBusiness((v) => !v)}>
            + New
          </button>
        </div>

        {showNewBusiness && (
          <form className="business-new-form" onSubmit={handleCreateBusiness}>
            <input className="form-input" autoFocus placeholder="Business name *" value={newBusiness.name} onChange={(e) => setNewBusiness((p) => ({ ...p, name: e.target.value }))} required />
            <input className="form-input" placeholder="Description" value={newBusiness.description} onChange={(e) => setNewBusiness((p) => ({ ...p, description: e.target.value }))} />
            <input className="form-input" placeholder="Website" value={newBusiness.website} onChange={(e) => setNewBusiness((p) => ({ ...p, website: e.target.value }))} />
            <input className="form-input" placeholder="Industry" value={newBusiness.industry} onChange={(e) => setNewBusiness((p) => ({ ...p, industry: e.target.value }))} />
            <select className="form-select" value={newBusiness.joinPolicy} onChange={(e) => setNewBusiness((p) => ({ ...p, joinPolicy: e.target.value }))}>
              <option value="request">Join requests</option>
              <option value="open">Open</option>
              <option value="invite">Invite only</option>
            </select>
            <button className="btn btn-primary btn-sm" disabled={savingBusiness} type="submit">
              {savingBusiness ? 'Creating...' : 'Create Business'}
            </button>
          </form>
        )}

        <ul className="businesses-list">
          {businesses.map((b) => (
            <li key={b._id}>
              <button
                className={`business-list-item ${selectedBusiness?._id === b._id ? 'active' : ''}`}
                onClick={() => openBusiness(b)}
              >
                <span className="business-avatar">{b.name?.[0]?.toUpperCase()}</span>
                <span className="business-list-info">
                  <span className="business-list-name">{b.name}</span>
                  {b.industry && <span className="business-list-meta">{b.industry}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* ── Main Detail View ── */}
      <main className="business-detail">
        {loading ? (
          <div className="business-detail-empty">
            <div>
              <h1>Loading businesses</h1>
              <p>Fetching businesses and workspace data...</p>
            </div>
          </div>
        ) : !selectedBusiness ? (
          <div className="business-detail-empty">
            <div>
              <h1>Businesses</h1>
              <p>Select a Business to see its Projects, people, Spaces and work that needs attention.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="business-detail-header">
              <div className="business-detail-title-block">
                <h1>{selectedBusiness.name}</h1>
                <p>{selectedBusiness.description || 'Legal & operational boundary'}</p>
              </div>
              <div className="business-detail-actions">
                {canManageProjects && (
                  <button className="btn btn-primary btn-sm" onClick={() => { setDetailTab('projects'); setShowNewProject((v) => !v); }}>
                    + New Project
                  </button>
                )}
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="business-tabs-bar">
              {visibleTabs.map((t) => (
                <button
                  key={t}
                  className={`business-tab-btn ${detailTab === t ? 'active' : ''}`}
                  onClick={() => setDetailTab(t)}
                >
                  {tabLabel(t)}
                </button>
              ))}
            </div>

            {/* ── OVERVIEW TAB ── */}
            {detailTab === 'overview' && (
              <div className="business-tab-content">
                <div className="business-attention-strip">
                  <div><span>Active Projects</span><strong>{activeProjects.length}</strong></div>
                  <div><span>Spaces</span><strong>{selectedBusiness.spaces?.length || 0}</strong></div>
                  <div><span>People</span><strong>{selectedBusiness.members?.length || 0}</strong></div>
                  {canManageMembers && <div><span>Join Requests</span><strong>{pendingJoinRequestCount}</strong></div>}
                  {canViewReporting && <div><span>Reporting</span><strong>{obligations.length || 'View Money'}</strong></div>}
                </div>

                {!isMember && selectedBusiness.joinPolicy !== 'invite' && (
                  <form className="business-request-form" onSubmit={handleRequestJoin}>
                    <h3>Request to join</h3>
                    <textarea className="form-textarea" rows={3} placeholder="Tell the business why you should join" value={joinMessage} onChange={(e) => setJoinMessage(e.target.value)} />
                    <button className="btn btn-primary btn-sm" type="submit">Request Join</button>
                  </form>
                )}

                <section className="business-section business-projects-preview">
                  <div className="business-section-heading-row">
                    <div>
                      <h3>Active Projects ({activeProjects.length})</h3>
                      <p className="panel-muted">Current work led by {selectedBusiness.name}</p>
                    </div>
                    <div className="business-section-actions">
                      {canManageProjects && <button className="btn btn-primary btn-sm" onClick={() => { setDetailTab('projects'); setShowNewProject(true); }}>+ New Project</button>}
                      <button className="btn btn-ghost btn-sm" onClick={() => setDetailTab('projects')}>View Projects</button>
                    </div>
                  </div>
                  <div className="business-project-cards">
                    {activeProjects.slice(0, 3).map((project) => (
                      <Link key={project._id} className="business-project-card" to={`/projects/${project._id}`}>
                        <strong>{project.name}</strong>
                        <span>{formatLifecycle(project.lifecycleState || project.status)}</span>
                      </Link>
                    ))}
                    {activeProjects.length === 0 && <p className="panel-muted">No active projects owned by this business yet.</p>}
                  </div>
                </section>

                <div className="business-overview-grid">
                  <section className="business-section">
                    <div className="business-section-heading-row">
                      <h3>Recent Spaces</h3>
                      <button className="btn btn-ghost btn-sm" onClick={() => setDetailTab('people')}>People & Spaces</button>
                    </div>
                    {recentSpaces.length === 0 ? (
                      <p className="panel-muted">No Spaces linked yet.</p>
                    ) : (
                      <div className="business-link-list">
                        {recentSpaces.map((space) => (
                          <div key={space._id} className="business-link-item">
                            <Link to={`/spaces/${space._id}`}>{space.name}</Link>
                            <span className="business-mini-meta">{space.type || 'Space'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  <section className="business-section">
                    <div className="business-section-heading-row">
                      <h3>People</h3>
                      <button className="btn btn-ghost btn-sm" onClick={() => setDetailTab('people')}>View People</button>
                    </div>
                    <ul className="business-people-preview">
                      {keyPeople.map((member) => (
                        <li key={String(member.user?._id || member.user)}>
                          <strong>{member.user?.name || 'Member'}</strong>
                          <span>{member.role}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                </div>

                <section className="business-section business-identity-card">
                  <h3>Business Details</h3>
                  <div className="business-summary-grid">
                    <div className="business-summary-item"><span>Website</span><strong>{selectedBusiness.website || 'Not set'}</strong></div>
                    <div className="business-summary-item"><span>Industry</span><strong>{selectedBusiness.industry || 'Not set'}</strong></div>
                    <div className="business-summary-item"><span>Owner</span><strong>{selectedBusiness.owner?.name || 'Unknown'}</strong></div>
                  </div>
                </section>
              </div>
            )}

            {/* ── PROJECTS TAB ── */}
            {detailTab === 'projects' && (
              <div className="business-tab-content">
                <div className="tab-section-header tab-section-header-row">
                  <div>
                    <h3>Projects</h3>
                    <p className="dashboard-sub">Projects owned and financially led by {selectedBusiness.name}</p>
                  </div>
                  {canManageProjects && (
                    <button className="btn btn-primary btn-sm" onClick={() => setShowNewProject((v) => !v)}>
                      + New Project
                    </button>
                  )}
                </div>

                {showNewProject && canManageProjects && (
                  <form className="business-inline-box" onSubmit={handleCreateProject}>
                    <h4>New Project</h4>
                    <div className="form-row">
                      <input
                        className="form-input"
                        placeholder="Project name *"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        required
                      />
                      <input
                        className="form-input"
                        placeholder="Short description"
                        value={newProjectDescription}
                        onChange={(e) => setNewProjectDescription(e.target.value)}
                      />
                      <button className="btn btn-primary btn-sm" type="submit" disabled={savingProject}>
                        {savingProject ? 'Creating...' : 'Create Project'}
                      </button>
                    </div>
                  </form>
                )}

                {(selectedBusiness.projects || []).length === 0 ? (
                  <p className="panel-muted">No projects owned by this business yet.</p>
                ) : (
                  <div className="business-project-cards">
                    {(selectedBusiness.projects || []).map((project) => (
                      <Link key={project._id} className="business-project-card" to={`/projects/${project._id}`}>
                        <div className="business-project-card-top">
                          <strong>{project.name}</strong>
                          <span className={`project-status-badge status-${project.lifecycleState || project.status}`}>
                            {formatLifecycle(project.lifecycleState || project.status)}
                          </span>
                        </div>
                        {project.description && <p>{project.description}</p>}
                        <div className="business-project-meta-grid">
                          <span>Lead: {project.leadBusinessId?.name || selectedBusiness.name}</span>
                          <span>Primary Space: {project.primarySpaceId?.name || 'None yet'}</span>
                          <span>Project Manager: {project.projectManagerIds?.[0]?.name || 'Not assigned'}</span>
                          <span>Participants: {(project.participatingBusinessIds || []).map((b) => b.name || 'Business').join(', ') || 'None'}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {canManageProjects && (
                <section className="business-section">
                  <h3>Add this Business to another Project</h3>
                  <p className="panel-muted">Use this only when this Business participates in a Project led elsewhere. Lead ownership stays unchanged.</p>
                  <form className="business-inline-form" onSubmit={handleLinkProject}>
                    <select className="form-select" value={linkProjectId} onChange={(e) => setLinkProjectId(e.target.value)}>
                      <option value="">Select project</option>
                      {projects
                        .filter((p) => String(p.leadBusinessId?._id || p.leadBusinessId || p.businessId) !== String(selectedBusiness._id))
                        .map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                    <button className="btn btn-ghost btn-sm" type="submit">Add as Participant</button>
                  </form>
                </section>
                )}
              </div>
            )}

            {/* ── PEOPLE TAB ── */}
            {detailTab === 'people' && (
              <div className="business-tab-content">
                {/* Members Section */}
                <section className="business-section">
                  <h3>Direct Members ({selectedBusiness.members?.length || 0})</h3>
                  {canManageMembers && (
                    <form className="business-inline-form" onSubmit={handleAddMember}>
                      <select className="form-select" value={memberUserId} onChange={(e) => setMemberUserId(e.target.value)}>
                        <option value="">Select user</option>
                        {visibleUsers.map((u) => <option key={u._id} value={u._id}>{u.name} — {u.email}</option>)}
                      </select>
                      <select className="form-select form-select-sm" value={memberRole} onChange={(e) => setMemberRole(e.target.value)}>
                        {roleOptionsForEditor.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <select className="form-select form-select-sm" value={memberTeamId} onChange={(e) => setMemberTeamId(e.target.value)}>
                        <option value="">No team</option>
                        {selectedTeams.map((team, idx) => <option key={`${team.name}-${idx}`} value={team._id || idx}>{team.name}</option>)}
                      </select>
                      <button className="btn btn-primary btn-sm" type="submit">Add Member</button>
                    </form>
                  )}
                  <ul className="business-member-list">
                    {(selectedBusiness.members || []).map((member) => {
                      const mId = member.user?._id || member.user;
                      const isOwner = member.role === 'owner';
                      const canEditThisMember = canManageMembers && !(isOwner && ownerCount <= 1);

                      return (
                        <li key={mId} className="business-member-item">
                          <div className="business-member-info">
                            <strong>{member.user?.name || 'Member'}</strong>
                            <span>{member.user?.email || ''}</span>
                          </div>
                          <div className="business-member-actions">
                            {canEditThisMember ? (
                              <select
                                className="form-select form-select-sm"
                                value={member.role}
                                onChange={(e) => handleUpdateMemberRole(mId, e.target.value)}
                                disabled={updatingMemberId === String(mId)}
                              >
                                {roleOptionsForEditor.map((r) => <option key={r} value={r}>{r}</option>)}
                              </select>
                            ) : (
                              <span className="business-member-role">{member.role}</span>
                            )}
                            {canManageMembers && !(isOwner && ownerCount <= 1) && (
                              <button className="btn btn-ghost btn-sm" onClick={() => handleRemoveMember(mId)}>Remove</button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>

                {/* Teams Section */}
                <section className="business-section">
                  <h3>Teams ({selectedTeams.length})</h3>
                  {canManageMembers && (
                    <form className="business-inline-form" onSubmit={handleAddTeam}>
                      <input className="form-input" placeholder="Team name *" value={teamName} onChange={(e) => setTeamName(e.target.value)} required />
                      <input className="form-input" placeholder="Description" value={teamDescription} onChange={(e) => setTeamDescription(e.target.value)} />
                      <button className="btn btn-primary btn-sm" type="submit">Add Team</button>
                    </form>
                  )}
                  {selectedTeams.length === 0 ? (
                    <p className="panel-muted">No teams created yet.</p>
                  ) : (
                    <div className="business-team-list">
                      {selectedTeams.map((team, idx) => (
                        <div key={idx} className="business-team-card">
                          <div className="business-team-header">
                            <div>
                              <strong>{team.name}</strong>
                              {team.description && <p>{team.description}</p>}
                            </div>
                            {canManageMembers && (
                              <button className="btn btn-ghost btn-sm" onClick={() => handleRemoveTeam(idx)}>Remove</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {/* Join Requests Section */}
                {selectedRequests.filter((r) => r.status === 'pending').length > 0 && canManageMembers && (
                  <section className="business-section">
                    <h3>Pending Join Requests</h3>
                    <ul className="business-request-list">
                      {selectedRequests.filter((r) => r.status === 'pending').map((req, idx) => (
                        <li key={idx} className="business-request-item">
                          <div>
                            <strong>{req.requester?.name || req.requester}</strong>
                            {req.message && <p>{req.message}</p>}
                          </div>
                          <div className="business-request-actions">
                            <button className="btn btn-primary btn-sm" onClick={() => handleApproveRequest(req.requester?._id || req.requester)}>Approve</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => handleRejectRequest(req.requester?._id || req.requester)}>Reject</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Linked Spaces */}
                <section className="business-section">
                  <h3>Spaces ({selectedBusiness.spaces?.length || 0})</h3>
                  {canManageMembers && (
                  <form className="business-inline-form" onSubmit={handleLinkSpace}>
                    <select className="form-select" value={linkSpaceId} onChange={(e) => setLinkSpaceId(e.target.value)}>
                      <option value="">Select space</option>
                      {spaces.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                    </select>
                    <button className="btn btn-primary btn-sm" type="submit">Link Space</button>
                  </form>
                  )}
                  <div className="business-link-list">
                    {(selectedBusiness.spaces || []).map((space) => (
                      <div key={space._id} className="business-link-item">
                        <Link to={`/spaces/${space._id}`}>{space.name}</Link>
                        {canManageMembers && <button className="btn btn-ghost btn-sm" onClick={() => handleUnlinkSpace(space._id)}>Unlink</button>}
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {/* ── MONEY TAB ── */}
            {detailTab === 'money' && (
              <div className="business-tab-content">
                <div className="tab-section-header">
                  <h3>Money</h3>
                  <p className="dashboard-sub">Funding, accounting attention and reporting deadlines for this Business</p>
                </div>

                {canViewAccounting && (
                <section className="business-section">
                  <h3>Needs Attention</h3>
                  {accountingLoading ? (
                    <p className="panel-muted">Loading money summary...</p>
                  ) : !accountingOverview ? (
                    <p className="panel-muted">No accounting summary available.</p>
                  ) : (
                    <div className="accounting-metrics-grid">
                      <div className="metric-box"><span className="metric-label">Projects</span><strong className="metric-val">{accountingOverview.projectsCount || 0}</strong></div>
                      <div className="metric-box"><span className="metric-label">Funding Sources</span><strong className="metric-val">{accountingOverview.fundingSourcesCount || 0}</strong></div>
                      <div className="metric-box"><span className="metric-label">Pending Expenses</span><strong className="metric-val">{accountingOverview.pendingExpensesCount || 0}</strong></div>
                      <div className="metric-box highlight"><span className="metric-label">Upcoming Reports</span><strong className="metric-val">{accountingOverview.upcomingReportingCount || 0}</strong></div>
                    </div>
                  )}
                </section>
                )}

                {canViewFunding && (
                <section className="business-section">
                  <div className="business-section-heading-row">
                    <h3>Funding</h3>
                  </div>
                  {canManageFunding && (
                <form className="business-inline-box" onSubmit={handleCreateFundingSource}>
                  <h4>New Funding Agreement</h4>
                  <div className="form-row">
                    <input className="form-input" placeholder="Agreement / Grant Name *" value={newFundingName} onChange={(e) => setNewFundingName(e.target.value)} required />
                    <input className="form-input" placeholder="Funding Body (e.g. FedNor)" value={newFundingBody} onChange={(e) => setNewFundingBody(e.target.value)} />
                    <input type="number" className="form-input form-input-sm" placeholder="Total Amount ($) *" value={newFundingAmount} onChange={(e) => setNewFundingAmount(e.target.value)} required />
                    <input className="form-input form-input-sm" placeholder="Agreement Ref #" value={newFundingRef} onChange={(e) => setNewFundingRef(e.target.value)} />
                    <button type="submit" className="btn btn-primary btn-sm">+ Create Source</button>
                  </div>
                </form>
                  )}

                {canManageFunding && fundingSources.length > 0 && (
                  <form className="business-inline-box" onSubmit={handleCreateAllocation}>
                    <h4>Allocate to Project</h4>
                    <div className="form-row">
                      <select className="form-select" value={allocSourceId} onChange={(e) => setAllocSourceId(e.target.value)} required>
                        <option value="">Select Funding Source...</option>
                        {fundingSources.map((s) => <option key={s._id} value={s._id}>{s.name} (${s.totalAmount?.toLocaleString()})</option>)}
                      </select>
                      <select className="form-select" value={allocProjectId} onChange={(e) => setAllocProjectId(e.target.value)} required>
                        <option value="">Select Target Project...</option>
                        {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                      </select>
                      <input type="number" className="form-input form-input-sm" placeholder="Allocation Amount ($)" value={allocAmount} onChange={(e) => setAllocAmount(e.target.value)} required />
                      <button type="submit" className="btn btn-primary btn-sm">Allocate</button>
                    </div>
                  </form>
                )}

                {fundingLoading ? (
                  <p className="panel-muted">Loading funding sources...</p>
                ) : fundingSources.length === 0 ? (
                  <p className="panel-muted">No funding sources recorded for this business.</p>
                ) : (
                  <ul className="funding-cards-list">
                    {fundingSources.map((fs) => (
                      <li key={fs._id} className="funding-card">
                        <div className="funding-card-top">
                          <strong>{fs.name || fs.program || 'Funding Source'}</strong>
                          <span className="funding-body-tag">{fs.fundingBody || fs.funderName || 'Funding'}</span>
                        </div>
                        <div className="funding-card-meta">
                          <span>Available: <strong>${(fs.availableAmount || fs.totalAmount || 0).toLocaleString()}</strong></span>
                          {(fs.agreementReference || fs.agreementNumber) && <span>Ref: {fs.agreementReference || fs.agreementNumber}</span>}
                          <span className={`status-pill ${fs.status === 'active' ? 'active' : ''}`}>{fs.status}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                </section>
                )}

                {canViewReporting && (
                <section className="business-section">
                  <h3>Reporting</h3>
                  {canManageReporting && (
                <form className="business-inline-box" onSubmit={handleCreateObligation}>
                  <h4>New Reporting Deadline</h4>
                  <div className="form-row">
                    <input className="form-input" placeholder="Title (e.g. Q3 Expenditure Report) *" value={newObligationTitle} onChange={(e) => setNewObligationTitle(e.target.value)} required />
                    <select className="form-select form-select-sm" value={newObligationType} onChange={(e) => setNewObligationType(e.target.value)}>
                      <option value="quarterly-expenditure">Quarterly Expenditure</option>
                      <option value="progress">Progress Report</option>
                      <option value="annual">Annual Report</option>
                      <option value="final">Final Report</option>
                      <option value="audited-financials">Audited Financials</option>
                    </select>
                    <select className="form-select" value={newObligationProjectId} onChange={(e) => setNewObligationProjectId(e.target.value)} required>
                      <option value="">Select Project *</option>
                      {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                    <input type="date" className="form-input form-input-sm" value={newObligationDueDate} onChange={(e) => setNewObligationDueDate(e.target.value)} required />
                    <button type="submit" className="btn btn-primary btn-sm">+ Create Obligation</button>
                  </div>
                </form>
                  )}

                {obligationsLoading ? (
                  <p className="panel-muted">Loading reporting deadlines...</p>
                ) : obligations.length === 0 ? (
                  <p className="panel-muted">No reporting deadlines recorded for this business.</p>
                ) : (
                  <table className="business-table">
                    <thead>
                      <tr>
                        <th>Obligation</th>
                        <th>Project</th>
                        <th>Type</th>
                        <th>Due Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {obligations.map((o) => (
                        <tr key={o._id}>
                          <td><strong>{o.title}</strong></td>
                          <td>{o.projectId?.name || 'Project'}</td>
                          <td><span className="obligation-type-pill">{o.type}</span></td>
                          <td>{new Date(o.dueDate).toLocaleDateString()}</td>
                          <td><span className={`status-pill ${o.status}`}>{o.status}</span></td>
                          <td>
                            {o.status === 'upcoming' && (
                              <button className="btn btn-xs btn-primary" onClick={() => handleUpdateObligationStatus(o._id, 'submitted')}>
                                Mark Submitted
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                </section>
                )}
              </div>
            )}

            {/* ── MORE TAB ── */}
            {detailTab === 'more' && (
              <div className="business-tab-content">
                {canManageMembers && (
                <section className="business-section">
                  <h3>Business Settings</h3>
                  {editing ? (
                    <form className="business-edit-form" onSubmit={handleSaveBusiness}>
                      <input className="form-input" value={editData.name} onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))} />
                      <textarea className="form-textarea" rows={3} value={editData.description} onChange={(e) => setEditData((p) => ({ ...p, description: e.target.value }))} />
                      <input className="form-input" value={editData.website} onChange={(e) => setEditData((p) => ({ ...p, website: e.target.value }))} placeholder="Website" />
                      <input className="form-input" value={editData.industry} onChange={(e) => setEditData((p) => ({ ...p, industry: e.target.value }))} placeholder="Industry" />
                      <select className="form-select" value={editData.joinPolicy} onChange={(e) => setEditData((p) => ({ ...p, joinPolicy: e.target.value }))}>
                        <option value="request">Requests reviewed</option>
                        <option value="open">Open to join</option>
                        <option value="invite">Invite only</option>
                      </select>
                      <div className="form-actions">
                        <button className="btn btn-ghost" type="button" onClick={() => setEditing(false)}>Cancel</button>
                        <button className="btn btn-primary" type="submit">Save Business</button>
                      </div>
                    </form>
                  ) : (
                    <div className="business-section-heading-row">
                      <div className="business-summary-grid business-summary-grid-compact">
                        <div className="business-summary-item"><span>How people join</span><strong>{formatJoinPolicy(selectedBusiness.joinPolicy)}</strong></div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Edit Settings</button>
                    </div>
                  )}
                </section>
                )}

                {canManageMembers && (
                <section className="business-section">
                  <h3>Relationships</h3>
                  <p className="panel-muted">Partner, funder, client, vendor and consultant links for this Business.</p>
                  <form className="business-inline-box" onSubmit={handleAddRelationship}>
                    <h4>Connect Business</h4>
                    <div className="form-row">
                      <select className="form-select" value={relTargetBizId} onChange={(e) => setRelTargetBizId(e.target.value)} required>
                        <option value="">Select target business...</option>
                        {businesses.filter((b) => b._id !== selectedBusiness._id).map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                      </select>
                      <select className="form-select" value={relType} onChange={(e) => setRelType(e.target.value)}>
                        {RELATIONSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <button type="submit" className="btn btn-primary btn-sm">Connect</button>
                    </div>
                  </form>

                  {relLoading ? <p className="panel-muted">Loading relationships...</p> : relationships.length === 0 ? <p className="panel-muted">No relationships recorded.</p> : (
                    <ul className="rel-list">
                      {relationships.map((r) => (
                        <li key={r._id} className="rel-card">
                          <div>
                            <strong>{r.relatedBusinessId?.name || 'Partner Business'}</strong>
                            <div className="rel-tags-row">{(r.relationshipTypes || []).map((t) => <span key={t} className="rel-type-pill">{t}</span>)}</div>
                          </div>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleArchiveRelationship(r._id)}>Archive</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                )}

                {canManageMembers && (
                <section className="business-section">
                  <h3>Additional Access</h3>
                  <p className="panel-muted">Temporary access for this Business, a Project, or a Space.</p>
                  <form className="business-inline-box" onSubmit={handleCreateRoleAssignment}>
                    <h4>Grant Access</h4>
                    <div className="form-row">
                      <select className="form-select" value={newRoleUserId} onChange={(e) => setNewRoleUserId(e.target.value)} required>
                        <option value="">Select person...</option>
                        {allUsers.map((u) => <option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}
                      </select>
                      <select className="form-select form-select-sm" value={newRoleScopeType} onChange={(e) => { setNewRoleScopeType(e.target.value); setNewRoleScopeId(''); }}>
                        {ACCESS_CONTEXT_TYPES.map((st) => <option key={st} value={st}>{st === 'business' ? 'This Business' : st.charAt(0).toUpperCase() + st.slice(1)}</option>)}
                      </select>
                      {newRoleScopeType === 'business' && <span className="business-inline-note">{selectedBusiness.name}</span>}
                      {newRoleScopeType === 'project' && (
                        <select className="form-select" value={newRoleScopeId} onChange={(e) => setNewRoleScopeId(e.target.value)} required>
                          <option value="">Select Project...</option>
                          {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                        </select>
                      )}
                      {newRoleScopeType === 'space' && (
                        <select className="form-select" value={newRoleScopeId} onChange={(e) => setNewRoleScopeId(e.target.value)} required>
                          <option value="">Select Space...</option>
                          {spaces.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                        </select>
                      )}
                      <select className="form-select form-select-sm" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} aria-label="Access role">
                        {ROLE_ASSIGNMENT_ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <select className="form-select form-select-sm" value={newRoleValidDays} onChange={(e) => setNewRoleValidDays(Number(e.target.value))} aria-label="Access ends">
                        <option value={7}>7 Days</option>
                        <option value={30}>30 Days</option>
                        <option value={90}>90 Days</option>
                        <option value={365}>1 Year</option>
                      </select>
                      <button type="submit" className="btn btn-primary btn-sm">Grant Access</button>
                    </div>
                  </form>

                  {roleLoading ? <p className="panel-muted">Loading access...</p> : roleAssignments.length === 0 ? <p className="panel-muted">No additional access recorded.</p> : (
                    <table className="business-table">
                      <thead><tr><th>Person</th><th>Access to</th><th>Role</th><th>Access ends</th><th>Status</th><th>Action</th></tr></thead>
                      <tbody>
                        {roleAssignments.map((a) => {
                          const isExpired = new Date(a.validUntil) < new Date();
                          const isRevoked = Boolean(a.revokedAt);
                          const isActive = !isExpired && !isRevoked;
                          return (
                            <tr key={a._id}>
                              <td><strong>{a.userId?.name || 'Person'}</strong></td>
                              <td><span className="scope-pill">{formatAccessScope(a.scopeType)}</span></td>
                              <td><span className="role-pill">{a.role}</span></td>
                              <td className="date-cell">{new Date(a.validUntil).toLocaleDateString()}</td>
                              <td><span className={`status-pill ${isActive ? 'active' : 'revoked'}`}>{isRevoked ? 'Revoked' : isExpired ? 'Expired' : 'Active'}</span></td>
                              <td>{isActive && <button className="btn btn-xs btn-danger" onClick={() => handleRevokeRole(a._id)}>Remove Access</button>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </section>
                )}

                {canManageDomains && (
                <section className="business-section">
                  <h3>Domains & Routing</h3>
                  <p className="panel-muted">Manage verified domains and where inbound addresses should land.</p>
                <form className="business-inline-box" onSubmit={handleCreateDomain}>
                  <h4>Add Business Domain</h4>
                  <div className="form-row">
                    <input className="form-input" placeholder="e.g. rsmin.ca *" value={newDomainName} onChange={(e) => setNewDomainName(e.target.value)} required />
                    <button type="submit" className="btn btn-primary btn-sm">+ Add Domain</button>
                  </div>
                </form>

                {domains.length > 0 && (
                  <ul className="domain-list">
                    {domains.map((d) => (
                      <li key={d._id} className="domain-item">
                        <div>
                          <strong>{d.domainName}</strong>
                          <span className={`domain-status ${d.verified ? 'verified' : 'pending'}`}>
                            {d.verified ? '✓ Verified' : 'Pending Verification'}
                          </span>
                        </div>
                        {!d.verified && (
                          <button className="btn btn-xs btn-ghost" onClick={() => handleVerifyDomain(d._id)}>
                            Verify Domain
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                <form className="business-inline-box" onSubmit={handleCreateAddressRoute}>
                  <h4>Create Inbound Address Route</h4>
                  <div className="form-row">
                    <input className="form-input" placeholder="e.g. intake@rsmin.ca *" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} required />
                    <select className="form-select form-select-sm" value={newAddressDomainId} onChange={(e) => setNewAddressDomainId(e.target.value)}>
                      <option value="">Select Domain (optional)</option>
                      {domains.map((d) => <option key={d._id} value={d._id}>{d.domainName}</option>)}
                    </select>
                    <select className="form-select form-select-sm" value={newAddressTargetType} onChange={(e) => setNewAddressTargetType(e.target.value)}>
                      <option value="space">Target: Space</option>
                      <option value="user">Target: User</option>
                    </select>
                    {newAddressTargetType === 'space' ? (
                      <select className="form-select" value={newAddressTargetId} onChange={(e) => setNewAddressTargetId(e.target.value)} required>
                        <option value="">Select Destination Space *</option>
                        {spaces.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                      </select>
                    ) : (
                      <select className="form-select" value={newAddressTargetId} onChange={(e) => setNewAddressTargetId(e.target.value)} required>
                        <option value="">Select Destination User *</option>
                        {allUsers.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
                      </select>
                    )}
                    <button type="submit" className="btn btn-primary btn-sm">+ Create Route</button>
                  </div>
                </form>

                {domainLoading ? (
                  <p className="panel-muted">Loading address routes...</p>
                ) : addressRoutes.length === 0 ? (
                  <p className="panel-muted">No address routes configured.</p>
                ) : (
                  <table className="business-table">
                    <thead>
                      <tr>
                        <th>Address</th>
                        <th>Destination</th>
                      </tr>
                    </thead>
                    <tbody>
                      {addressRoutes.map((r) => (
                        <tr key={r._id}>
                          <td><strong>{r.address}</strong></td>
                          <td><span className="route-target-pill">{r.targetType}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                </section>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}